<template>
  <base-resize-observer
    @resize="$emit('resize', $event)"
  >
    <div
      :data-id="itemId"
      :data-index="realIndex"
      class="base-virtual-scroll-viewport-item"
      data-component="grouped-by-contact-session-list-item"
    >
      <keep-alive>
        <slot
          v-if="isActivated"
          name="default"
        />
        <div
          v-else
          class="base-virtual-scroll-viewport-item-placeholder"
          :style="{ height: `${itemSize}px` }"
        />
      </keep-alive>
    </div>
  </base-resize-observer>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  toRefs,
  watch,
  getCurrentInstance,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
} from '@vue/composition-api';
import { BaseResizeObserver } from '../decorators';
import {
  IVScrollItem,
} from '../../types';

const DEFAULT_RENDER_DELAY = 300;

export default defineComponent({
  name: 'BaseVirtualScrollViewportItem',
  components: {
    BaseResizeObserver,
  },
  props: {
    item: {
      type: Object as PropType<IVScrollItem>,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    itemSize: {
      type: Number,
      required: true,
    },
    realIndex: {
      type: Number,
      required: true,
    },
    activeItemId: {
      type: [Number, String],
      default: undefined,
    },
    renderDelay: {
      type: Number,
      default: DEFAULT_RENDER_DELAY,
    },
    isScrolling: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['activation', 'deactivation', 'resize'],
  setup(props) {
    const {
      renderDelay,
      isScrolling,
    } = toRefs(props);
    const vm = getCurrentInstance()!;

    const isActivated = ref(false);
    let timer: number;

    const activate = () => {
      timer = window.setTimeout(async () => {
        isActivated.value = true;
        vm.proxy.$emit('activation');
      }, renderDelay.value);
    };

    const cancel = () => {
      clearInterval(timer);
    };

    onActivated(() => {
      if (!isScrolling.value) {
        activate();
      }
    });

    onDeactivated(() => {
      cancel();
      isActivated.value = false;
      vm.proxy.$emit('deactivation');
    });

    onBeforeUnmount(() => {
      cancel();
    });

    watch(isScrolling, () => {
      if (isScrolling.value) {
        cancel();
      } else if (!vm.isDeactivated) {
        activate();
      }
    });

    return {
      isActivated,
      activate,
      cancel,
    };
  },
});
</script>
